import { useRef, useState, type DragEvent } from "react";
import { PLANNING_KIND_LABELS, planningDay, type PlanningPeriod } from "@klantpunt/shared";
import { Meta } from "../../app/Meta";
import { PlanningMark, STATUS_DOT } from "./PlanningMark";
import { appointmentText, cellMoments, cellPlan, movedStart, type PlanRow, type RowGroup } from "./planningView";

/** Wanneer, in woorden: bij elke regel onder de titel. */
export function whenText({ l, status }: PlanRow) {
  if (status === "klaar") return "Klaar";
  const a = l.appointment;
  if (a && status === "na_vervaldatum")
    return `Gepland ${planningDay(a.startOn)}, na de vervaldatum ${planningDay(l.dueOn!)}`;
  if (a && status === "gepland") return `Gepland ${planningDay(a.startOn)}${a.confirmed ? "" : ", voorlopig"}`;
  if (status === "verlopen" && l.lastResult === "afgekeurd") return "Afgekeurd, opnieuw uitvoeren";
  if (!l.dueOn) return "Datum onbekend";
  if (status === "verlopen") return `Verlopen sinds ${planningDay(l.dueOn)}`;
  if (status === "te_plannen") return `Te plannen, uiterlijk ${planningDay(l.dueOn)}`;
  return `Volgende keer ${planningDay(l.dueOn)}`;
}

export const lineMeta = ({ l }: PlanRow, withBuilding: boolean) => [
  withBuilding && l.building.code,
  l.asset && `${l.asset.code} ${l.asset.name}`,
  l.code,
  PLANNING_KIND_LABELS[l.kind],
  (l.appointment?.performer ?? l.performer)?.name,
];

/** Spans van de bovenste kopregel: maanden (jaar) of weken (maand). */
function spans(period: PlanningPeriod) {
  const out: { label: string; n: number }[] = [];
  for (const c of period.columns) {
    const last = out[out.length - 1];
    if (last && last.label === c.group) last.n++;
    else out.push({ label: c.group, n: 1 });
  }
  return out;
}

/**
 * Kruisjesplanning op desktop: links de regel (vast bij horizontaal scrollen), rechts een kolom per week
 * (jaar) of dag (maand, week) met ✓ uitgevoerd, ✕ volgende keer en lichte ✕ voor de prognose. Een afspraak is
 * een blok (vol = bevestigd, gestreept = voorlopig) dat je naar een andere week of dag sleept. Met het
 * toetsenbord verplaats je via de regel (het venster).
 */
export function PlanningGrid({
  period,
  groups,
  today,
  groupBy,
  onOpen,
  onMove,
}: {
  period: PlanningPeriod;
  groups: RowGroup[];
  today: string;
  groupBy: "gebouw" | "uitvoerder" | "soort";
  onOpen: (r: PlanRow) => void;
  onMove: (r: PlanRow, startOn: string) => void;
}) {
  const cols = period.columns;
  // Slepen: regel en de cel waar het blok werd opgepakt; alleen binnen dezelfde regel neerzetten.
  const drag = useRef<{ id: string; from: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const dropOk = (r: PlanRow) => drag.current?.id === r.l.id;
  const onDrop = (e: DragEvent, r: PlanRow, cell: string) => {
    e.preventDefault();
    setOver(null);
    const d = drag.current;
    drag.current = null;
    if (!d || d.id !== r.l.id || d.from === cell || !r.l.appointment) return;
    onMove(r, movedStart(r.l.appointment.startOn, d.from, cell));
  };
  const isToday = (i: number) => cols[i]!.start <= today && today <= cols[i]!.end;
  const groupStart = (i: number) => i > 0 && cols[i]!.group !== cols[i - 1]!.group;
  const cls = (i: number) =>
    [isToday(i) && "kp-today", cols[i]!.weekend && "kp-we", groupStart(i) && "kp-gs"].filter(Boolean).join(" ");
  return (
    <div className="kp-gridwrap" role="region" aria-label={`Planning ${period.title}`} tabIndex={0}>
      <table className={`kp-grid kp-v-${period.view}`} style={{ "--kp-n": cols.length } as React.CSSProperties}>
        <thead>
          <tr className="kp-groups">
            <th className="kp-lab" rowSpan={2} scope="col">
              <span className="label">Wat</span>
            </th>
            {spans(period).map((s, i) => (
              <th key={i} colSpan={s.n} scope="colgroup" className={i > 0 ? "kp-gs" : undefined}>
                <span className="label">{s.label}</span>
              </th>
            ))}
          </tr>
          <tr className="kp-cols">
            {cols.map((c, i) => (
              <th key={c.start} scope="col" className={cls(i)} aria-current={isToday(i) ? "date" : undefined}>
                {c.sub && <span className="kp-sub">{c.sub}</span>}
                <span className="num">{c.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.key}>
            <tr className="kp-ghead">
              <th colSpan={cols.length + 1} scope="colgroup">
                <span className="kp-gtitle">
                  <strong>{g.title}</strong>
                  {g.sub && <span className="muted">{g.sub}</span>}
                  <span className="muted num">{g.rows.length}</span>
                </span>
              </th>
            </tr>
            {g.rows.map((r) => (
              <tr key={r.l.id} className={`kp-row kp-row-${r.status}`}>
                <th scope="row" className="kp-lab">
                  <button type="button" className="kp-line" onClick={() => onOpen(r)}>
                    <i className={`obdot ${STATUS_DOT[r.status]}`} aria-hidden="true" />
                    <span className="kp-ltext">
                      <span className="kp-title">{r.l.title}</span>
                      <Meta className="small muted" items={lineMeta(r, groupBy !== "gebouw")} />
                      <span className={`small kp-when kp-when-${r.status}`}>{whenText(r)}</span>
                    </span>
                  </button>
                </th>
                {cols.map((c, i) => {
                  const m = cellMoments(r.moments, c.start, c.end);
                  const dates = (t: string) => m.dates.filter((d) => d.type === t).map((d) => d.date);
                  const plan = cellPlan(r.l, c.start, c.end);
                  const a = r.l.appointment;
                  const key = `${r.l.id}|${c.start}`;
                  return (
                    <td
                      key={c.start}
                      className={[cls(i), over === key && "kp-drop"].filter(Boolean).join(" ")}
                      onDragOver={(e) => {
                        if (!dropOk(r)) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (over !== key) setOver(key);
                      }}
                      onDragLeave={() => over === key && setOver(null)}
                      onDrop={(e) => onDrop(e, r, c.start)}
                    >
                      {plan && a && (
                        <span
                          className={[
                            "kp-blk",
                            a.confirmed ? "kp-blk-ok" : "kp-blk-tent",
                            r.status === "na_vervaldatum" && "kp-blk-late",
                            plan.first && "kp-blk-s",
                            plan.last && "kp-blk-e",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          draggable={r.l.canPlan}
                          title={`Gepland ${appointmentText(a)}${r.l.canPlan ? ". Sleep om te verplaatsen." : ""}`}
                          {...(plan.first
                            ? { role: "img", "aria-label": `Gepland ${appointmentText(a)}` }
                            : { "aria-hidden": true })}
                          onDragStart={(e) => {
                            drag.current = { id: r.l.id, from: c.start };
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", r.l.id);
                          }}
                          onDragEnd={() => {
                            drag.current = null;
                            setOver(null);
                          }}
                        />
                      )}
                      {m.done && <PlanningMark type="done" status={r.status} dates={dates("done")} />}
                      {m.due && !m.done && <PlanningMark type="due" status={r.status} dates={dates("due")} />}
                      {m.forecast && !m.due && !m.done && (
                        <PlanningMark type="forecast" status={r.status} dates={dates("forecast")} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
